import { brandCatalogIdentity } from './brandCatalogIdentity';

export type StagedStorefrontPlatformId = 'shopify' | 'woocommerce' | 'vendor_api';

export interface StagedStorefrontPlatform {
  id: StagedStorefrontPlatformId;
  label: string;
  hint: string;
}

export interface StagedStorefrontInput {
  brandId?: string;
  brand?: string;
  source?: {
    connectorId?: string;
    sourceUrl?: string;
  };
  variants?: Array<{ source?: string }>;
}

const PLATFORMS: Record<StagedStorefrontPlatformId, Omit<StagedStorefrontPlatform, 'id'>> = {
  shopify: {
    label: 'Shopify',
    hint: 'Ingested from a Shopify storefront (products.json or /products/ PDP)',
  },
  woocommerce: {
    label: 'WooCommerce',
    hint: 'Ingested from a WooCommerce catalog (/product/ or wp-json)',
  },
  vendor_api: {
    label: 'Vendor API',
    hint: 'Ingested from the manufacturer’s official product API, not a storefront scrape',
  },
};

function catalogNote(input: StagedStorefrontInput): string {
  return (brandCatalogIdentity(input.brandId, input.brand)?.source || '').toLowerCase();
}

function blob(input: StagedStorefrontInput): string {
  return [
    input.source?.connectorId,
    input.source?.sourceUrl,
    ...(input.variants || []).map((row) => row.source),
    catalogNote(input),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/**
 * Best-effort storefront / ingest platform for the Source URL row.
 * Returns undefined when we do not have a confident signal.
 */
export function stagedStorefrontPlatform(
  input: StagedStorefrontInput | null | undefined,
): StagedStorefrontPlatform | undefined {
  if (!input) return undefined;
  const url = String(input.source?.sourceUrl || '').toLowerCase();
  const connector = String(input.source?.connectorId || '').toLowerCase();
  const note = catalogNote(input);
  const haystack = blob(input);

  if (
    /myshopify\.com|cdn\.shopify\.com|\/cdn\/shop\//.test(url) ||
    /\bshopify\b/.test(connector) ||
    (/\/products\/[^/?#]+/.test(url) && !/\bwoo\b/.test(note)) ||
    (/\bshopify\b/.test(note) && !/\bwoo\b/.test(note))
  ) {
    return { id: 'shopify', ...PLATFORMS.shopify };
  }

  if (
    /\/wp-json\/wc\/|\/wp-content\/|[?&]add-to-cart=|woocommerce/.test(url) ||
    /\bwoo\b/.test(connector) ||
    /\bwoo\b/.test(note)
  ) {
    return { id: 'woocommerce', ...PLATFORMS.woocommerce };
  }

  // Woo PDPs are `/product/slug`; Shopify is `/products/handle`.
  if (/\/product\/[^/?#]+/.test(url) && !/\/products\//.test(url) && /\bwoo\b/.test(haystack)) {
    return { id: 'woocommerce', ...PLATFORMS.woocommerce };
  }

  if (
    (/\bapi\b/.test(note) && !/shopify|woo|products\.json/.test(note)) ||
    (input.variants || []).some((row) => /\bapi\b/.test(String(row.source || '')))
  ) {
    return { id: 'vendor_api', ...PLATFORMS.vendor_api };
  }

  return undefined;
}
